import React, { useState, useMemo, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Tabs,
  Text,
  Badge,
  Button,
  Card,
  Table,
  StatsGrid,
  StatusDot,
  VersionTimeline,
} from '@teals/ui-web';
import type { TabItem } from '@teals/ui-web';
import type { Column } from '@teals/ui-web';
import type { VersionEntry, StatItem } from '@teals/ui-web';

type DetailTab = 'overview' | 'configure' | 'playground';

interface AgentConfig {
  systemPrompt: string;
  model: string;
  temperature: number;
}

interface AssociatedApplication {
  id: string;
  name: string;
  status: 'Active' | 'Inactive';
  sessions: number;
  lastActive: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

const MOCK_AGENT = {
  id: 'agent-1',
  name: 'Customer Support',
  scope: 'global' as const,
  version: 'published' as const,
  description: 'Handles customer inquiries, ticket routing, and FAQ responses.',
};

const MOCK_STATS: StatItem[] = [
  { label: 'Total Sessions', value: '2,847' },
  { label: 'Exchanges', value: '18,392' },
  { label: 'Avg Response Time', value: '1.2s' },
  { label: 'Active Applications', value: '12' },
];

const MOCK_VERSIONS: VersionEntry[] = [
  { version: 'v1.3', status: 'draft', date: 'Jun 24, 2026', description: 'Temperature tuning and prompt refinement', changeCount: 3 },
  { version: 'v1.2', status: 'sandbox', date: 'Jun 20, 2026', description: 'Added escalation workflow' },
  { version: 'v1.1', status: 'published', date: 'Jun 15, 2026', description: 'Initial production release' },
];

const MOCK_APPLICATIONS: AssociatedApplication[] = [
  { id: 'a-1', name: 'Acme Support App', status: 'Active', sessions: 1240, lastActive: '2 min ago' },
  { id: 'a-2', name: 'Globex Portal', status: 'Active', sessions: 876, lastActive: '15 min ago' },
  { id: 'a-3', name: 'Initech Bot', status: 'Active', sessions: 432, lastActive: '1 hour ago' },
  { id: 'a-4', name: 'Stark Assistant', status: 'Inactive', sessions: 299, lastActive: '3 days ago' },
];

const INITIAL_CONFIG: AgentConfig = {
  systemPrompt:
    'You are a helpful customer support agent for Luminafactory. Answer questions accurately and concisely. Escalate complex issues to a human agent when confidence is below 70%.',
  model: 'claude-sonnet-4-20250514',
  temperature: 0.7,
};

const TABS: TabItem[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'configure', label: 'Configure' },
  { id: 'playground', label: 'Playground' },
];

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 1200px;
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: none;
  border: none;
  color: var(--accent);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: var(--accent-hover);
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const TabContent = styled.div`
  margin-top: 0.5rem;
`;

const OverviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const SectionLabel = styled.h3`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.75rem;
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const ConfigPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
  }
`;

const EditorPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 180px;
  padding: 0.75rem 1rem;
  border: 2px solid var(--border);
  border-radius: 0.5rem;
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--text-primary);
  background: var(--bg-surface);
  resize: vertical;
  outline: none;
  line-height: 1.6;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--accent);
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem 0.75rem;
  min-height: 44px;
  border: 2px solid var(--border);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);
  background: var(--bg-surface);
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--accent);
  }
`;

const SliderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Slider = styled.input`
  flex: 1;
  accent-color: var(--accent);
`;

const SliderValue = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  min-width: 2.5rem;
  text-align: right;
`;

const PreviewCard = styled(Card)`
  position: sticky;
  top: 1rem;
`;

const JsonPre = styled.pre`
  margin: 0;
  font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', monospace;
  font-size: 0.8125rem;
  color: var(--text-primary);
  line-height: 1.6;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ConfigActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 500px;
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  overflow: hidden;
  background: var(--bg-surface);
`;

const ChatBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: var(--bg-elevated, #f8fafc);
  border-bottom: 1px solid var(--border);
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const MessageBubble = styled.div<{ $role: 'user' | 'assistant' }>`
  max-width: 75%;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5;
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  background: ${({ $role }) =>
    $role === 'user' ? 'var(--accent)' : 'var(--bg-elevated, #f1f5f9)'};
  color: ${({ $role }) =>
    $role === 'user' ? 'white' : 'var(--text-primary)'};
`;

const ChatInputRow = styled.form`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--border);
`;

const ChatInput = styled.input`
  flex: 1;
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);
  background: transparent;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--accent);
  }

  &::placeholder {
    color: var(--text-secondary);
  }
`;

const SCOPE_BADGES: Record<string, { variant: 'primary' | 'info' | 'warning'; label: string }> = {
  global: { variant: 'primary', label: 'Global' },
  organisation: { variant: 'info', label: 'Organisation' },
  application: { variant: 'warning', label: 'Application' },
};

const AgentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const [config, setConfig] = useState<AgentConfig>(INITIAL_CONFIG);
  const [messages, setMessages] = useState<ChatMessage[]>([
    { id: '1', role: 'assistant', content: 'Hello! I\'m the Customer Support agent. How can I help you today?' },
  ]);
  const [chatInput, setChatInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const agent = { ...MOCK_AGENT, id: id ?? MOCK_AGENT.id };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    const userMsg: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: chatInput.trim(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setChatInput('');

    setTimeout(() => {
      const reply: ChatMessage = {
        id: `msg-${Date.now() + 1}`,
        role: 'assistant',
        content:
          'Thank you for your message. This is a simulated response. Connect to the WebSocket endpoint to test with the live agent.',
      };
      setMessages((prev) => [...prev, reply]);
    }, 800);
  };

  const configJson = useMemo(
    () =>
      JSON.stringify(
        {
          agent_id: agent.id,
          system_prompt: config.systemPrompt,
          model: config.model,
          temperature: config.temperature,
        },
        null,
        2
      ),
    [agent.id, config]
  );

  const applicationColumns: Column<AssociatedApplication>[] = useMemo(
    () => [
      { key: 'name', header: 'Application' },
      {
        key: 'status',
        header: 'Status',
        render: (val: string) => (
          <Badge variant={val === 'Active' ? 'success' : 'neutral'} size="sm">
            {val}
          </Badge>
        ),
      },
      {
        key: 'sessions',
        header: 'Sessions',
        align: 'right' as const,
        render: (val: number) => val.toLocaleString(),
      },
      { key: 'lastActive', header: 'Last Active' },
    ],
    []
  );

  const scopeCfg = SCOPE_BADGES[agent.scope] ?? SCOPE_BADGES.global;

  return (
    <Page>
      <BackLink onClick={() => navigate('/agents')}>
        &larr; Back to Agents
      </BackLink>

      <HeaderRow>
        <Text variant="h3">{agent.name}</Text>
        <StatusDot status={agent.version} label={agent.version} />
        <Badge variant={scopeCfg.variant} size="sm">
          {scopeCfg.label}
        </Badge>
      </HeaderRow>

      <Tabs
        items={TABS}
        activeTab={activeTab}
        onChange={(t) => setActiveTab(t as DetailTab)}
        variant="underline"
      />

      <TabContent>
        {activeTab === 'overview' && (
          <OverviewSection>
            <div>
              <SectionLabel>Performance</SectionLabel>
              <StatsGrid items={MOCK_STATS} columns={4} />
            </div>

            <TwoColumn>
              <div>
                <SectionLabel>Version History</SectionLabel>
                <VersionTimeline versions={MOCK_VERSIONS} />
              </div>
              <div>
                <SectionLabel>Associated Applications</SectionLabel>
                <Table columns={applicationColumns} data={MOCK_APPLICATIONS} rowKey="id" />
              </div>
            </TwoColumn>
          </OverviewSection>
        )}

        {activeTab === 'configure' && (
          <ConfigPanel>
            <EditorPane>
              <div>
                <FieldLabel>System Prompt</FieldLabel>
                <TextArea
                  value={config.systemPrompt}
                  onChange={(e) =>
                    setConfig((c) => ({ ...c, systemPrompt: e.target.value }))
                  }
                />
              </div>

              <div>
                <FieldLabel>Model</FieldLabel>
                <Select
                  value={config.model}
                  onChange={(e) =>
                    setConfig((c) => ({ ...c, model: e.target.value }))
                  }
                >
                  <option value="claude-sonnet-4-20250514">Claude Sonnet 4</option>
                  <option value="claude-3-5-haiku-20241022">Claude 3.5 Haiku</option>
                  <option value="gpt-4o">GPT-4o</option>
                  <option value="gpt-4o-mini">GPT-4o Mini</option>
                </Select>
              </div>

              <SliderWrapper>
                <FieldLabel>Temperature</FieldLabel>
                <SliderRow>
                  <Slider
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={config.temperature}
                    onChange={(e) =>
                      setConfig((c) => ({
                        ...c,
                        temperature: parseFloat(e.target.value),
                      }))
                    }
                  />
                  <SliderValue>{config.temperature.toFixed(1)}</SliderValue>
                </SliderRow>
              </SliderWrapper>

              <ConfigActions>
                <Button variant="outline">Save Draft</Button>
                <Button variant="primary">Publish</Button>
              </ConfigActions>
            </EditorPane>

            <PreviewCard padding={20} elevation="sm">
              <SectionLabel>Configuration Preview</SectionLabel>
              <JsonPre>{configJson}</JsonPre>
            </PreviewCard>
          </ConfigPanel>
        )}

        {activeTab === 'playground' && (
          <ChatContainer>
            <ChatBanner>
              <StatusDot status="info" size="sm" />
              Connect to WebSocket to test your agent
            </ChatBanner>
            <MessageList>
              {messages.map((msg) => (
                <MessageBubble key={msg.id} $role={msg.role}>
                  {msg.content}
                </MessageBubble>
              ))}
              <div ref={messagesEndRef} />
            </MessageList>
            <ChatInputRow onSubmit={handleSendMessage}>
              <ChatInput
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Type a message..."
              />
              <Button type="submit" variant="primary" size="sm" disabled={!chatInput.trim()}>
                Send
              </Button>
            </ChatInputRow>
          </ChatContainer>
        )}
      </TabContent>
    </Page>
  );
};

export default AgentDetail;
